import type { KeyboardEvent, RefObject } from 'react'
import React from 'react'
import { Select as SutraSelect } from 'sutra/components/Select/Select'
import { Toggle } from 'sutra/components/Toggle/Toggle'

import { PanelDateRangeControl } from 'components/date-range-picker/PanelDateRangeControl/PanelDateRangeControl'
import { SutraSearch } from 'components/ui/SutraSearch'
import { UndoRedoButtons } from 'components/UndoRedoButtons'
import { LENS_LABEL } from 'features/catalog/constants'
import {
  LensSelectWrap,
  SearchWrapper,
  Toolbar,
  ToolbarToggleControl,
} from 'features/catalog/features/table/components/CatalogTableToolbar/catalog-table-toolbar.styles'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import type { UseSnapshotHistoryReturn } from 'hooks/use-snapshot-history'
import mixpanel from 'trackers/mixpanel'

const LENS_OPTIONS = (
  ['product_hierarchy', 'brand', 'source_type', 'theme'] as const
).map((value) => ({ value, label: LENS_LABEL[value] ?? value }))

type Props = {
  selectedLens: string
  onSelectLens: (lens: string) => void
  panel: ChartPanel
  updatePanel: (panelId: string, updates: Partial<ChartPanel>) => void
  groupCountries: boolean
  onGroupCountriesChange: (value: boolean) => void
  groupThemes: boolean
  onGroupThemesChange: (value: boolean) => void
  searchTerm: string
  onSearchTermChange: (value: string) => void
  onSearchKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void
  searchInputRef: RefObject<HTMLInputElement>
  history: UseSnapshotHistoryReturn
}

/** The row above the table: date range, the View toggle (products/brands/
 * sources/themes), search, and a lens-specific toggle (Group countries on
 * Sources, Group themes on Themes). Extracted as its own sibling so
 * `CatalogTable.tsx` stays under the component-file-too-large threshold. */
export const CatalogTableToolbar: React.FC<Props> = ({
  selectedLens,
  onSelectLens,
  panel,
  updatePanel,
  groupCountries,
  onGroupCountriesChange,
  groupThemes,
  onGroupThemesChange,
  searchTerm,
  onSearchTermChange,
  onSearchKeyDown,
  searchInputRef,
  history,
}) => (
  <Toolbar>
    <UndoRedoButtons {...history} />
    <PanelDateRangeControl
      dateRange={panel.dateRange}
      onChange={(dateRange) => updatePanel(panel.id, { dateRange })}
      surface="catalog"
      location="toolbar"
    />
    <LensSelectWrap>
      <SutraSelect
        value={selectedLens}
        onChange={(value) => {
          mixpanel.track('catalog', { action: 'lens', value })
          onSelectLens(value)
        }}
        options={LENS_OPTIONS}
      />
    </LensSelectWrap>
    <SearchWrapper>
      <SutraSearch
        ref={searchInputRef}
        placeholder={
          selectedLens === 'product_hierarchy'
            ? 'Search name or URL...'
            : 'Search...'
        }
        value={searchTerm}
        onChange={(e) => onSearchTermChange(e.target.value)}
        onKeyDown={onSearchKeyDown}
        shortcutHint="/"
      />
    </SearchWrapper>
    {selectedLens === 'source_type' && (
      <ToolbarToggleControl>
        <Toggle
          checked={groupCountries}
          onChange={onGroupCountriesChange}
          aria-label="Group countries"
        />
        <span>Group countries</span>
      </ToolbarToggleControl>
    )}
    {selectedLens === 'theme' && (
      <ToolbarToggleControl>
        <Toggle
          checked={groupThemes}
          onChange={onGroupThemesChange}
          aria-label="Group themes"
        />
        <span>Group themes</span>
      </ToolbarToggleControl>
    )}
  </Toolbar>
)
